import { useAuth } from '@client/hooks/useAuth';
import { useStoreMutation } from '@client/queries/useStoreMutation';
import { useStoreQuery } from '@client/queries/useStoreQuery';
import {
  createScheduler,
  deleteEntitySchedulers,
  deleteScheduler,
  getAllSchedulers,
  getScheduler,
  getSchedulers,
  updateScheduler,
} from '@client/services/scheduler.service';
import { isNewMode } from '@client/utils/form.util';

import type { TPaginateProps } from '@client/types/antd.type';
import type {
  TSchedulerEntityPaths,
  ESchedulerPrefix,
  IObjectId,
  IScheduler,
} from '@common/types';
import type { TablePaginationConfig } from 'antd/es/table/interface';

// Query keys
export const schedulerKeys = {
  all: ['schedulers'] as const,
  lists: () => [...schedulerKeys.all, 'list'] as const,
  list: (filters: Record<string, any>) =>
    [...schedulerKeys.lists(), filters] as const,
  details: () => [...schedulerKeys.all, 'detail'] as const,
  detail: (id: string) => [...schedulerKeys.details(), id] as const,
};

export const useSchedulerQueries = () => {
  const { user } = useAuth();

  // Get all schedulers
  const useAllSchedulers = (params?: TPaginateProps & TablePaginationConfig) =>
    useStoreQuery({
      queryKey: schedulerKeys.list({ type: 'all', ...params }),
      queryFn: () => getAllSchedulers(params),
    });

  // Get user schedulers
  const useEntitySchedulers = <T extends IObjectId>(
    entityId: T['id'],
    entityPath: TSchedulerEntityPaths,
    schedulerType: ESchedulerPrefix,
    params?: TPaginateProps & TablePaginationConfig,
  ) =>
    useStoreQuery({
      queryKey: schedulerKeys.list({
        path: entityPath,
        id: entityId,
        schedulerType,
        ...params,
      }),
      queryFn: () => getSchedulers(entityId, entityPath, schedulerType, params),
      enabled: !!user && !isNewMode(entityId),
    });

  // Get scheduler by id
  const useEntityScheduler = (id: string) =>
    useStoreQuery({
      queryKey: schedulerKeys.detail(id),
      queryFn: () => getScheduler(id),
      enabled: !!id,
    });

  // Create a scheduler mutation
  const useCreateScheduler = <T extends IObjectId>(
    entityPath: TSchedulerEntityPaths,
  ) =>
    useStoreMutation({
      mutationFn: ({
        entityId,
        data,
      }: {
        entityId: T['id'];
        data: IScheduler;
      }) => createScheduler(data, entityId, entityPath),
      invalidateQueries: [schedulerKeys.lists()],
    });

  // Update scheduler mutation
  const useUpdateScheduler = () =>
    useStoreMutation({
      mutationFn: ({ id, data }: { id: string; data: IScheduler }) =>
        updateScheduler(id, data),
      invalidateQueries: [schedulerKeys.lists()],
    });

  // Delete scheduler mutation
  const useDeleteScheduler = () =>
    useStoreMutation({
      mutationFn: (id: string) => deleteScheduler(id),
      invalidateQueries: [schedulerKeys.lists()],
    });

  // Delete all entity schedulers mutation
  const useDeleteEntitySchedulers = <T extends IObjectId>(
    entityPath: TSchedulerEntityPaths,
  ) =>
    useStoreMutation({
      mutationFn: (entityId: T['id']) =>
        deleteEntitySchedulers<T>(entityId, entityPath),
      invalidateQueries: [schedulerKeys.lists()],
    });

  return {
    useAllSchedulers,
    useEntitySchedulers,
    useEntityScheduler,
    useCreateScheduler,
    useUpdateScheduler,
    useDeleteScheduler,
    useDeleteEntitySchedulers,
  };
};
